/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

// -----------------------------------------------------------------------------
// Image protection
// -----------------------------------------------------------------------------

/**
 * Deters casual copying of store imagery: right-click save, drag-to-desktop,
 * long-press save on mobile, and middle-click "open image in new tab".
 *
 * IMPORTANT: this is deterrence, not security. A browser cannot display an image
 * without downloading it first, so the file is always reachable through DevTools,
 * the network cache, or the CDN URL directly, and no amount of client-side code
 * changes that. Screenshots are always possible. The goal is to stop the easy
 * paths that account for most casual copying, without breaking real features.
 *
 * Deliberately NOT done here:
 *   - Blocking DevTools / F12 / Ctrl+Shift+I. Trivially bypassed, and it breaks
 *     the browser for legitimate users (including accessibility tooling).
 *   - Transparent overlay <div>s on top of images. They swallow the clicks that
 *     product zoom, carousel swipes and gallery links depend on.
 *   - Disabling text selection page-wide, which hurts real customers copying a
 *     product name or an address.
 */
(() => {
  /** Interactive contexts whose own click/drag behavior must keep working. */
  const INTERACTIVE = 'a, button, [is="toggle-button"], [is="toggle-link"], .pswp, .flickity-viewport';

  const isProtectedImage = (target) => target instanceof Element && target.matches('img, picture, svg');

  // Right-click and mobile long-press (which surfaces the same "Save image" menu).
  document.addEventListener(
    'contextmenu',
    (event) => {
      if (isProtectedImage(event.target)) event.preventDefault();
    },
    { capture: true }
  );

  // Dragging an image to the desktop or another tab saves a copy of it.
  document.addEventListener(
    'dragstart',
    (event) => {
      if (isProtectedImage(event.target)) event.preventDefault();
    },
    { capture: true }
  );

  // Middle-click opens the image in a background tab when it is not inside a link.
  document.addEventListener(
    'auxclick',
    (event) => {
      if (event.button === 1 && isProtectedImage(event.target) && !event.target.closest(INTERACTIVE)) {
        event.preventDefault();
      }
    },
    { capture: true }
  );

  // iOS shows its own save/copy callout on long-press; this is the only way to
  // suppress it, and it is scoped to images so page text stays selectable.
  const style = document.createElement('style');
  style.textContent = `
    img, picture, svg {
      -webkit-touch-callout: none;
      -webkit-user-drag: none;
      user-select: none;
      -webkit-user-select: none;
    }
  `;
  document.head.appendChild(style);
})();